import React, { useCallback, useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { apiV2 } from "@/api/axiosConfig";
import { isAxiosError } from "axios";
import { ButtonV2 } from "@/components/ui/ButtonsV2/ButtonV2";
import { useAuth } from "@/context/user";
import { Client } from "@/types/userManagement";
import { ClientSelectV2 } from "@/lib/clients/ClientSelectV2";

type ParsedRow = {
    name?: string;
    countryISO?: string;
    region?: string;
    county?: string;
    town?: string;
    postalCode?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    latitude?: string | number;
    longitude?: string | number;
    guardianIDs?: string;
    [key: string]: any;
};

type ImportRow = ParsedRow & {
    rowNumber: number;
    status: "pending" | "processing" | "success" | "error";
    error?: string;
    facilityID?: string;
};

type ImportStats = {
    total: number;
    pending: number;
    processing: number;
    success: number;
    error: number;
};

export const ImportFacilities: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prevUrl = searchParams.get("prev") || "/facilities";
    const { isSuperAdmin } = useAuth();

    const [client, setClient] = useState<Client | null | undefined>(undefined);
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [parsedData, setParsedData] = useState<ImportRow[]>([]);
    const parsedDataRef = useRef<ImportRow[]>([]);
    const [isImporting, setIsImporting] = useState(false);
    const [stats, setStats] = useState<ImportStats>({
        total: 0,
        pending: 0,
        processing: 0,
        success: 0,
        error: 0
    });

    // Keep ref in sync with state
    useEffect(() => {
        parsedDataRef.current = parsedData;
    }, [parsedData]);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const parseFile = useCallback((uploadedFile: File) => {
        const fileExtension = uploadedFile.name.split(".").pop()?.toLowerCase();

        if (fileExtension === "csv") {
            // Parse CSV
            Papa.parse(uploadedFile, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const rows: ImportRow[] = results.data.map((row: any, index: number) => ({
                        ...row,
                        rowNumber: index + 1,
                        status: "pending" as const
                    }));
                    setParsedData(rows);
                    setStats({
                        total: rows.length,
                        pending: rows.length,
                        processing: 0,
                        success: 0,
                        error: 0
                    });
                },
                error: (error) => {
                    alert(`Error parsing CSV: ${error.message}`);
                }
            });
        } else if (fileExtension === "xlsx" || fileExtension === "xls") {
            // Parse Excel
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: "array" });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet);

                    const rows: ImportRow[] = jsonData.map((row: any, index: number) => ({
                        ...row,
                        rowNumber: index + 1,
                        status: "pending" as const
                    }));
                    setParsedData(rows);
                    setStats({
                        total: rows.length,
                        pending: rows.length,
                        processing: 0,
                        success: 0,
                        error: 0
                    });
                } catch (error) {
                    alert(`Error parsing Excel file: ${error}`);
                }
            };
            reader.readAsArrayBuffer(uploadedFile);
        } else {
            alert("Please upload a CSV or Excel file");
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            parseFile(droppedFile);
        }
    }, [parseFile]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseFile(selectedFile);
        }
    }, [parseFile]);

    const updateRowStatus = useCallback((index: number, status: ImportRow["status"], error?: string, facilityID?: string) => {
        const oldStatus = parsedDataRef.current[index]?.status;

        setParsedData(prev => {
            const newData = [...prev];
            newData[index] = { ...newData[index], status, error, facilityID };
            return newData;
        });

        // Update stats based on the actual old status from ref
        setStats(prevStats => {
            const newStats = { ...prevStats };
            if (oldStatus) newStats[oldStatus]--;
            newStats[status]++;
            return newStats;
        });
    }, []);

    const validateRow = (row: ParsedRow): { valid: boolean; error?: string } => {
        if (!row.name || row.name.toString().trim() === "") {
            return { valid: false, error: "Facility name is required" };
        }

        const lat = parseFloat(row.latitude as string);
        const lng = parseFloat(row.longitude as string);

        if (isNaN(lat) || isNaN(lng)) {
            return { valid: false, error: "Valid latitude and longitude are required" };
        }

        if (lat < -90 || lat > 90) {
            return { valid: false, error: "Latitude must be between -90 and 90" };
        }

        if (lng < -180 || lng > 180) {
            return { valid: false, error: "Longitude must be between -180 and 180" };
        }

        return { valid: true };
    };

    const importRow = useCallback(async (row: ParsedRow): Promise<{ success: boolean; error?: string; facilityID?: string }> => {
        try {
            // Validate row
            const validation = validateRow(row);
            if (!validation.valid) {
                return { success: false, error: validation.error };
            }

            // Build address lines array
            const addressLines = [
                row.addressLine1,
                row.addressLine2,
                row.addressLine3
            ].filter(line => line && line.toString().trim() !== "");

            // Parse guardian IDs (comma-separated string or array)
            let guardianIDs: string[] = [];
            if (row.guardianIDs) {
                if (typeof row.guardianIDs === "string") {
                    guardianIDs = row.guardianIDs.split(",").map(id => id.trim()).filter(id => id);
                } else if (Array.isArray(row.guardianIDs)) {
                    guardianIDs = row.guardianIDs;
                }
            }

            const payload = {
                clientID: client?.id || null,
                name: row.name,
                address: {
                    countryISO: row.countryISO || "",
                    region: row.region || "",
                    county: row.county || "",
                    town: row.town || "",
                    postalCode: row.postalCode || "",
                    addressLines,
                },
                latitude: parseFloat(row.latitude as string),
                longitude: parseFloat(row.longitude as string),
                guardianIDs
            };

            // Try to update first (if ID provided), otherwise create
            if (row.id) {
                const response = await apiV2.patch(`facilities/${row.id}`, payload);
                return { success: true, facilityID: row.id as string };
            } else {
                const response = await apiV2.post("facilities", payload);
                return { success: true, facilityID: response.data.id };
            }
        } catch (error) {
            if (isAxiosError(error) && error?.response?.data?.error) {
                return { success: false, error: error.response.data.error };
            }
            return { success: false, error: "Failed to import facility" };
        }
    }, [client]);

    const handleStartImport = useCallback(async () => {
        setIsImporting(true);

        // Process rows one by one using ref data
        const dataToProcess = parsedDataRef.current;
        for (let i = 0; i < dataToProcess.length; i++) {
            const row = dataToProcess[i];

            // Update status to processing
            updateRowStatus(i, "processing");

            // Import the row
            const result = await importRow(row);

            // Update status based on result
            if (result.success) {
                updateRowStatus(i, "success", undefined, result.facilityID);
            } else {
                updateRowStatus(i, "error", result.error);
            }
        }

        setIsImporting(false);
    }, [updateRowStatus, importRow]);

    const handleReset = useCallback(() => {
        setFile(null);
        setParsedData([]);
        setStats({
            total: 0,
            pending: 0,
            processing: 0,
            success: 0,
            error: 0
        });
    }, []);

    const handleDownloadExample = useCallback(() => {
        // Create example data with all columns
        const exampleData = [
            {
                id: "",
                name: "Example Facility 1",
                latitude: 51.5074,
                longitude: -0.1278,
                countryISO: "GB",
                region: "England",
                county: "Greater London",
                town: "London",
                postalCode: "SW1A 1AA",
                addressLine1: "123 Example Street",
                addressLine2: "Building A",
                addressLine3: "",
                guardianIDs: "guardian-id-1,guardian-id-2"
            },
            {
                id: "",
                name: "Example Facility 2",
                latitude: 40.7128,
                longitude: -74.0060,
                countryISO: "US",
                region: "New York",
                county: "New York County",
                town: "New York",
                postalCode: "10001",
                addressLine1: "456 Sample Avenue",
                addressLine2: "",
                addressLine3: "",
                guardianIDs: ""
            }
        ];

        // Convert to CSV using papaparse
        const csv = Papa.unparse(exampleData);

        // Create blob and download
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", "facility-import-example.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const getStatusColor = (status: ImportRow["status"]) => {
        switch (status) {
            case "pending": return "border-light-grey bg-desaturated-grey";
            case "processing": return "border-blue bg-light-blue";
            case "success": return "border-green bg-desaturated-green";
            case "error": return "border-red bg-desaturated-red";
            default: return "border-light-grey";
        }
    };

    const getStatusBadge = (status: ImportRow["status"]) => {
        switch (status) {
            case "pending":
                return <span className="px-2 py-1 text-xs rounded-full bg-light-grey text-grey">Pending</span>;
            case "processing":
                return <span className="px-2 py-1 text-xs rounded-full bg-light-blue text-dark-blue">Processing...</span>;
            case "success":
                return <span className="px-2 py-1 text-xs rounded-full bg-desaturated-green text-dark-green">Success</span>;
            case "error":
                return <span className="px-2 py-1 text-xs rounded-full bg-desaturated-red text-dark-red">Error</span>;
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <Link to={prevUrl} className="hover:text-grey">Facilities</Link>
                <span>&gt;</span>
                <span className="text-grey">Import Facilities</span>
            </nav>
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold">Import Facilities</h1>
                <div className="flex gap-2">
                    <ButtonV2
                        label="Download Example"
                        onClick={handleDownloadExample}
                    />
                </div>
            </div>

            {/* Client Selector */}
            {isSuperAdmin && (
                <div className="bg-white shadow-md rounded-lg p-6">
                    <ClientSelectV2
                        onChange={setClient}
                        label="Client"
                        required
                    />
                </div>
            )}

            {/* Upload Area */}
            {!file && (
                <div
                    className={`bg-white shadow-md rounded-lg p-12 border-2 border-dashed transition-colors ${
                        isDragging ? "border-blue bg-light-blue" : "border-light-grey"
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className="flex flex-col items-center gap-4">
                        <div className="text-6xl text-light-grey">📄</div>
                        <div className="text-center">
                            <p className="text-lg font-semibold mb-2">Drop your CSV or Excel file here</p>
                            <p className="text-grey mb-4">or</p>
                            <label className="cursor-pointer">
                                <span className="px-6 py-3 bg-blue text-white rounded-md hover:bg-dark-blue inline-block">
                                    Browse Files
                                </span>
                                <input
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </label>
                        </div>
                        <div className="text-sm text-grey text-center mt-4">
                            <p className="font-semibold mb-2">Expected columns:</p>
                            <p className="text-xs">
                                id (for updates), name*, latitude*, longitude*, countryISO, region, county, town,
                                postalCode, addressLine1, addressLine2, addressLine3, guardianIDs
                            </p>
                            <p className="text-xs mt-1">* Required fields</p>
                            <p className="text-xs mt-2 text-blue">
                                Click &quot;Download Example&quot; above to get a sample CSV file
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview and Import */}
            {file && parsedData.length > 0 && (
                <>
                    {/* Stats */}
                    <div className="bg-white shadow-md rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">
                                {file.name} ({stats.total} rows)
                            </h2>
                            <div className="flex gap-2">
                                <ButtonV2
                                    label="Reset"
                                    onClick={handleReset}
                                    disabled={isImporting}
                                />
                                {!isImporting && stats.pending === stats.total && (
                                    <ButtonV2
                                        label="Start Import"
                                        onClick={handleStartImport}
                                        disabled={isSuperAdmin && !client}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Warning message if super admin hasn't selected client */}
                        {isSuperAdmin && !client && (
                            <div className="mb-4 p-3 bg-light-orange border border-dark-orange rounded-md text-dark-orange">
                                Please select a client before starting the import.
                            </div>
                        )}

                        {/* Progress Stats */}
                        <div className="grid grid-cols-4 gap-4 mb-4">
                            <div className="bg-desaturated-grey p-4 rounded-md">
                                <div className="text-2xl font-bold text-grey">{stats.pending}</div>
                                <div className="text-sm text-grey">Pending</div>
                            </div>
                            <div className="bg-light-blue p-4 rounded-md">
                                <div className="text-2xl font-bold text-dark-blue">{stats.processing}</div>
                                <div className="text-sm text-dark-blue">Processing</div>
                            </div>
                            <div className="bg-desaturated-green p-4 rounded-md">
                                <div className="text-2xl font-bold text-dark-green">{stats.success}</div>
                                <div className="text-sm text-dark-green">Success</div>
                            </div>
                            <div className="bg-desaturated-red p-4 rounded-md">
                                <div className="text-2xl font-bold text-dark-red">{stats.error}</div>
                                <div className="text-sm text-dark-red">Error</div>
                            </div>
                        </div>
                    </div>

                    {/* Data Preview - Grid Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {parsedData.map((row, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-lg shadow-md border-2 p-4 transition-all ${getStatusColor(row.status)}`}
                            >
                                {/* Header */}
                                <div className="flex justify-between items-start mb-3 pb-2 border-b">
                                    <div>
                                        <div className="text-xs text-grey mb-1">Row {row.rowNumber}</div>
                                        <div className="font-semibold text-lg">
                                            {row.name || <span className="text-red">Missing Name</span>}
                                        </div>
                                    </div>
                                    {getStatusBadge(row.status)}
                                </div>

                                {/* Details */}
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-grey">Coordinates:</span>
                                        <span className="ml-2 font-medium">
                                            {row.latitude && row.longitude
                                                ? `${row.latitude}, ${row.longitude}`
                                                : <span className="text-red">Missing</span>
                                            }
                                        </span>
                                    </div>

                                    {(row.addressLine1 || row.town || row.region) && (
                                        <div>
                                            <span className="text-grey">Address:</span>
                                            <div className="ml-2 text-xs text-grey mt-1">
                                                {row.addressLine1 && <div>{row.addressLine1}</div>}
                                                {row.addressLine2 && <div>{row.addressLine2}</div>}
                                                {row.town && <div>{row.town}{row.region ? `, ${row.region}` : ""}</div>}
                                                {row.countryISO && <div>{row.countryISO}</div>}
                                            </div>
                                        </div>
                                    )}

                                    {row.guardianIDs && (
                                        <div>
                                            <span className="text-grey">Guardian IDs:</span>
                                            <span className="ml-2 text-xs">{row.guardianIDs}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Error Message */}
                                {row.error && (
                                    <div className="mt-3 pt-3 border-t border-red">
                                        <div className="text-xs font-semibold text-dark-red mb-1">Error:</div>
                                        <div className="text-xs text-dark-red">{row.error}</div>
                                    </div>
                                )}

                                {/* Success Info */}
                                {row.status === "success" && row.facilityID && (
                                    <div className="mt-3 pt-3 border-t border-green">
                                        <div className="text-xs text-dark-green">
                                            Facility ID: {row.facilityID}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
